import React, { useState } from 'react';
import { Checkbox } from '@myua/kit';
import { MagicTemplate } from '../model';
import { TM } from '../tokens';
import { Field, MagicBtn } from './ui';

// Sub-modal to edit a template's name, hint and prompt. Built-in templates
// can be edited; custom ones can also be deleted. `isNew` marks a draft from
// "New" that only joins the list on save.
export const TemplateEditor: React.FC<{
  tpl: MagicTemplate;
  isNew?: boolean;
  onSave: (tpl: MagicTemplate) => void;
  onCancel: () => void;
  onDelete: (() => void) | null;
}> = ({ tpl, isNew, onSave, onCancel, onDelete }) => {
  const [label, setLabel] = useState(tpl.label);
  const [hint, setHint] = useState(tpl.hint);
  const [prompt, setPrompt] = useState(tpl.prompt);
  const [postUpscale, setPostUpscale] = useState(!!tpl.postUpscale);
  const [postRemoveBg, setPostRemoveBg] = useState(!!tpl.postRemoveBg);

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.35)',
        zIndex: 70,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 520,
          background: '#fff',
          borderRadius: 10,
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        <div
          style={{
            padding: '14px 18px',
            borderBottom: `1px solid ${TM.border}`,
            fontSize: 14,
            fontWeight: 700,
            color: TM.text,
          }}
        >
          {isNew ? 'New template' : 'Edit template'}
        </div>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Name">
            <input
              className="al-magic-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </Field>
          <Field label="Hint (shown on hover in the template list)">
            <input
              className="al-magic-input"
              value={hint}
              placeholder="What this mode does, in one line"
              onChange={(e) => setHint(e.target.value)}
            />
          </Field>
          <Field label="Prompt (system instruction added on top of what you type)">
            <textarea
              className="al-magic-prompt al-magic-prompt_tall"
              value={prompt}
              placeholder="Describe how the model should treat the references…"
              onChange={(e) => setPrompt(e.target.value)}
              rows={5}
            />
          </Field>
          <Field label="After generation (on-device, applied in order)">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Checkbox checked={postUpscale} onChange={(e) => setPostUpscale(e.target.checked)}>
                <span style={{ fontSize: 12, color: TM.text }}>Upscale 2×</span>
              </Checkbox>
              <Checkbox checked={postRemoveBg} onChange={(e) => setPostRemoveBg(e.target.checked)}>
                <span style={{ fontSize: 12, color: TM.text }}>Remove background</span>
              </Checkbox>
            </div>
            <div style={{ fontSize: 11, color: TM.textFaint, marginTop: 6, lineHeight: 1.4 }}>
              {postUpscale && postRemoveBg
                ? 'The result is upscaled, then its background is removed — automatically, free, on your device.'
                : 'Run free on-device tools on the result automatically after this template generates.'}
            </div>
          </Field>
          <div style={{ fontSize: 11, color: TM.textFaint, lineHeight: 1.45 }}>
            Templates are saved <b style={{ color: TM.text }}>on this device</b> — your edits and
            your own templates come back next time you open the studio. Shared, admin-editable
            templates (and their preset references) come in a later iteration.
          </div>
        </div>
        <div
          style={{
            padding: '12px 18px',
            borderTop: `1px solid ${TM.border}`,
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            background: TM.blueWash,
          }}
        >
          {onDelete && (
            <MagicBtn kind="danger" size="sm" onClick={onDelete}>
              Delete
            </MagicBtn>
          )}
          <div style={{ flex: 1 }} />
          <MagicBtn kind="ghost" onClick={onCancel}>
            Cancel
          </MagicBtn>
          <MagicBtn
            kind="primary"
            onClick={() =>
              onSave({
                // An all-blank name would leave an unclickable-looking row.
                ...tpl,
                label: label.trim() || 'Untitled template',
                hint,
                prompt,
                postUpscale,
                postRemoveBg,
              })
            }
          >
            Save
          </MagicBtn>
        </div>
      </div>
    </div>
  );
};
